import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Share2, Check } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

// Fallback local data
import { updates as localUpdates } from '../data/unifiedEcosystem';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Homepage OG image
const OG_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/b2d972ec-c236-4c9d-bcb4-767b73cc1dfb/images/943e531631512ed0fda889d8f1a4e45ce9f7d8a6d2e014af06fbfc80639da43f.png';

// Convert Google Drive URLs to reliable thumbnail URLs
const convertGoogleDriveUrl = (url) => {
  if (!url) return url;
  if (url.includes('drive.google.com/thumbnail') || url.includes('lh3.googleusercontent.com')) return url;
  
  const fileMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) return `https://drive.google.com/thumbnail?id=${fileMatch[1]}&sz=w1000`;
  
  const openMatch = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
  if (openMatch) return `https://drive.google.com/thumbnail?id=${openMatch[1]}&sz=w1000`;
  
  const ucMatch = url.match(/drive\.google\.com\/uc\?(?:export=view&)?id=([a-zA-Z0-9_-]+)/);
  if (ucMatch) return `https://drive.google.com/thumbnail?id=${ucMatch[1]}&sz=w1000`;
  
  return url;
};

// Share button component for content cards
const ShareButton = ({ slug, title }) => {
  const [copied, setCopied] = useState(false);

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const shareUrl = `${window.location.origin}/api/og/${slug}`;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      onClick={handleShare}
      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider font-medium transition-all ${
        copied 
          ? 'bg-green-500/20 text-green-500 border border-green-500/30' 
          : 'bg-background/90 hover:bg-background text-foreground border border-border hover:border-foreground/50'
      }`}
      title={`Share "${title}" with thumbnail preview`}
      data-testid={`share-btn-${slug}`}
    >
      {copied ? (
        <>
          <Check size={12} />
          <span>Copied!</span>
        </>
      ) : (
        <>
          <Share2 size={12} />
          <span>Share</span>
        </>
      )}
    </button>
  );
};

export const HomePage = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);

  const filters = ['All', 'Press', 'Updates', 'Essays'];

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/content?limit=50`);
        if (response.ok) {
          const data = await response.json();
          // Map API fields to match local data structure
          const mapped = data.map(item => ({
            ...item,
            id: item.content_id,
            externalUrl: item.external_url
          }));
          // Sort by date descending (most recent first)
          mapped.sort((a, b) => new Date(b.date) - new Date(a.date));
          setUpdates(mapped);
        } else {
          // Fallback to local data
          setUpdates(localUpdates);
        }
      } catch (error) {
        // Fallback to local data
        setUpdates(localUpdates);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  const filterContent = () => {
    let filtered = updates;
    
    if (activeFilter !== 'All') {
      // Map filter names to category values
      const categoryMap = {
        'Press': ['Press'],
        'Updates': ['Release', 'Update', 'Announcement'],
        'Essays': ['Essay', 'BTS']
      };
      
      const categories = categoryMap[activeFilter] || [];
      filtered = updates.filter(item => categories.includes(item.category));
    }
    
    // Always ensure sorted by date descending (most recent first)
    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const featuredItem = updates.find(item => item.featured);
  const filteredItems = filterContent().filter(item => !item.featured);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="homepage">
      <Helmet>
        <title>Cultural Revolution | Creation. Momentum. Ownership.</title>
        <meta name="description" content="Cultural Revolution is an editorial hub for creator-led projects in film, media, and culture. Creation. Momentum. Ownership." />
        <meta property="og:title" content="Cultural Revolution" />
        <meta property="og:description" content="Creation. Momentum. Ownership." />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cultural Revolution" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Cultural Revolution" />
        <meta name="twitter:description" content="Creation. Momentum. Ownership." />
        <meta name="twitter:image" content={OG_IMAGE} />
      </Helmet>
      
      {/* Tagline */}
      <section className="editorial-container py-12 border-b border-border">
        <p className="text-xs uppercase tracking-widest text-center text-muted-foreground font-medium">
          Creation. Momentum. <span className="accent-red">Ownership.</span>
        </p>
      </section>

      {/* Hero - Single Featured Project - Compact */}
      {featuredItem && (
        <section className="editorial-container py-12 lg:py-16">
          <div className="block group" data-testid="featured-item">
            <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
              {/* Image - Smaller */}
              <Link to={`/${featuredItem.slug}`} className="lg:col-span-2 relative aspect-[2/3] overflow-hidden bg-black">
                <img
                  src="https://images.squarespace-cdn.com/content/v1/6462caf4ffff997cb8dd6e8c/813a12be-f880-499a-b6b7-9149ebf87ec2/YMH+Title+Treatment+White.png"
                  alt={featuredItem.title}
                  className="w-full h-full object-contain p-8"
                />
              </Link>

              {/* Content - Compact */}
              <div className="lg:col-span-3 flex flex-col justify-center space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-widest accent-red">
                    Featured
                  </span>
                  <ShareButton slug={featuredItem.slug} title={featuredItem.title} />
                </div>
                
                <Link to={`/${featuredItem.slug}`}>
                  <h1 
                    className="text-3xl sm:text-4xl lg:text-5xl editorial-serif leading-none hover:accent-red transition-colors"
                    data-testid="featured-title"
                  >
                    {featuredItem.title}
                  </h1>
                </Link>
                
                <p className="text-base text-foreground/80 leading-relaxed">
                  {featuredItem.dek}
                </p>
                
                <div className="flex items-center gap-3 text-xs text-muted-foreground uppercase tracking-wider">
                  <span>{featuredItem.project}</span>
                  <span>·</span>
                  <span>{new Date(featuredItem.date).getFullYear()}</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Transition - Compact */}
      <section className="editorial-container">
        <div className="h-px bg-border"></div>
        <div className="py-8 text-center">
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
            In Motion
          </p>
        </div>
      </section>

      {/* Filter Tabs - Compact */}
      <section className="editorial-container py-4 sticky top-16 lg:top-20 bg-background/98 backdrop-blur-md z-40 border-b border-border">
        <nav className="flex items-center gap-12 overflow-x-auto" data-testid="filter-nav">
          {filters.map((filter) => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`text-xs uppercase tracking-widest whitespace-nowrap pb-1 font-medium transition-colors ${ 
                activeFilter === filter
                  ? 'accent-red border-b-2 border-accent-red'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`filter-${filter.toLowerCase()}`}
            >
              {filter}
            </button>
          ))}
        </nav>
      </section>

      {/* Editorial Feed - All items link to canonical pages */}
      <section className="editorial-container py-20 lg:py-32">
        {filteredItems.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-20">
            {filteredItems.map((item) => (
              <article 
                key={item.id || item.content_id} 
                className="block group"
                data-testid={`feed-item-${item.slug}`}
              >
                {/* Image with Share Button Overlay */}
                <div className="relative aspect-[4/3] mb-6 overflow-hidden bg-black">
                  <Link to={`/${item.slug}`}>
                    <img
                      src={convertGoogleDriveUrl(item.image)}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-103 opacity-90"
                    />
                  </Link>
                  {/* Share button overlay */}
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ShareButton slug={item.slug} title={item.title} />
                  </div>
                </div>

                {/* Content */}
                <div className="space-y-3">
                  {/* Type & Source */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-widest">
                      <span className="px-2 py-0.5 bg-primary/10 text-primary">{item.type}</span>
                      {item.source && (
                        <>
                          <span>·</span>
                          <span>{item.source}</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Headline */}
                  <Link to={`/${item.slug}`}>
                    <h3 className="text-2xl lg:text-3xl editorial-serif leading-tight group-hover:accent-red transition-colors">
                      {item.title}
                    </h3>
                  </Link>
                  
                  {/* Dek */}
                  <p className="text-sm text-foreground/70 leading-relaxed line-clamp-2">
                    {item.dek}
                  </p>
                  
                  {/* Project & Date */}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-widest">
                    {item.project && (
                      <>
                        <span>{item.project}</span>
                        <span>·</span>
                      </>
                    )}
                    <span className="flex items-center gap-1">
                      <Calendar size={10} />
                      {new Date(item.date).toLocaleDateString('en-US', { 
                        month: 'short', 
                        year: 'numeric' 
                      })}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="text-center py-32">
            <p className="text-muted-foreground text-sm uppercase tracking-widest">No entries</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default HomePage;
